import { useEffect, useState } from 'react';
import { types as commonTypes, hooks as commonHooks } from '@sitebuilder/common';
import MonthSelect from '../PricesTable/MonthSelect';
import DaySelect from '../PricesTable/DaySelect';
import YearSelect from '../PricesTable/YearSelect';

export interface Date {
    day: number;
    month: number;
    year: number;
}

interface DatePickerProps {
    minDate: number;
    maxDate: number;
    startDateLabel: string;
    endDateLabel: string;
    startDayAriaLabel?: string;
    startMonthAriaLabel?: string;
    startYearAriaLabel?: string;
    endDayAriaLabel?: string;
    endMonthAriaLabel?: string;
    endYearAriaLabel?: string;
    onDateChange: (s: Date, e: Date) => void;
    extremes: Highcharts.AxisSetExtremesEventObject;
}

interface PropsShape extends commonTypes.WidgetInstanceProps, DatePickerProps {}

const DatePicker = (props: PropsShape) => {
    const { data, startDateLabel, endDateLabel, ...rest } = props;
    const i18n = commonHooks.useLocalization();

    const minDate = new Date(rest.minDate);
    const maxDate = new Date(rest.maxDate);

    const [startDate, setStartDate] = useState<Date>({
        day: minDate.getDate(),
        month: minDate.getMonth(),
        year: minDate.getFullYear(),
    });
    const [endDate, setEndDate] = useState<Date>({
        day: maxDate.getDate(),
        month: maxDate.getMonth(),
        year: maxDate.getFullYear(),
    });

    const _startDate = new Date(startDate.year, startDate.month, startDate.day);
    const _endDate = new Date(endDate.year, endDate.month, endDate.day);

    useEffect(() => {
        if (rest.extremes && rest.extremes.userMin && rest.extremes.userMax) {
            const userMinDate = new Date(rest.extremes.userMin).toUTCString();
            const userMaxDate = new Date(rest.extremes.userMax).toUTCString();

            if (userMinDate) {
                setStartDate({
                    day: new Date(userMinDate).getUTCDate(),
                    month: new Date(userMinDate).getUTCMonth(),
                    year: new Date(userMinDate).getUTCFullYear(),
                });
            }

            if (userMaxDate) {
                setEndDate({
                    day: new Date(userMaxDate).getUTCDate(),
                    month: new Date(userMaxDate).getUTCMonth(),
                    year: new Date(userMaxDate).getUTCFullYear(),
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest.extremes]);

    const onStartDateChange = (e: React.ChangeEvent<HTMLSelectElement>, key: string) => {
        const value: number = parseFloat(e.target.value);
        rest.onDateChange({ ...startDate, [key]: value }, endDate);
        setStartDate({ ...startDate, [key]: value });
    };

    const onEndDateChange = (e: React.ChangeEvent<HTMLSelectElement>, key: string) => {
        const value: number = parseFloat(e.target.value);
        rest.onDateChange(startDate, { ...endDate, [key]: value });
        setEndDate({ ...endDate, [key]: value });
    };

    return (
        <div className="date-picker-conatiner">
            <div className="start-date-container">
                <label
                    className="start-date-label"
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(startDateLabel || '', data),
                    }}
                />
                <br />
                <MonthSelect
                    selectedDate={_startDate}
                    id={'growthchart-start-month'}
                    onChange={onStartDateChange}
                    ariaLabel={i18n.format(rest.startMonthAriaLabel || '', data)}
                />
                <DaySelect
                    selectedDate={_startDate}
                    id={'growthchart-start-day'}
                    onChange={onStartDateChange}
                    ariaLabel={i18n.format(rest.startDayAriaLabel || '', data)}
                />
                <YearSelect
                    minDate={minDate}
                    maxDate={maxDate}
                    selectedDate={_startDate}
                    id={'growthchart-start-year'}
                    onChange={onStartDateChange}
                    ariaLabel={i18n.format(rest.startYearAriaLabel || '', data)}
                />
            </div>
            <div className="end-date-container">
                <label
                    className="start-date-label"
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(endDateLabel || '', data),
                    }}
                />
                <br />
                <MonthSelect
                    selectedDate={_endDate}
                    id={'growthchart-end-month'}
                    onChange={onEndDateChange}
                    ariaLabel={i18n.format(rest.endMonthAriaLabel || '', data)}
                />
                <DaySelect
                    selectedDate={_endDate}
                    id={'growthchart-end-day'}
                    onChange={onEndDateChange}
                    ariaLabel={i18n.format(rest.endDayAriaLabel || '', data)}
                />
                <YearSelect
                    minDate={minDate}
                    maxDate={maxDate}
                    selectedDate={_endDate}
                    id={'growthchart-end-year'}
                    onChange={onEndDateChange}
                    ariaLabel={i18n.format(rest.endYearAriaLabel || '', data)}
                />
            </div>
        </div>
    );
};

export default DatePicker;
